/**
 * Notifications bell on the home page greeting bar.
 *
 * Click → popover listing the family's risk alerts from the last 7 days
 * (the same feed RiskAlertsCard renders), each with a severity-tinted
 * chip and one-line advice. Footer link jumps to the full timeline.
 * The orange unread dot reads any non-empty alert list.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BellRing, ShieldAlert, ShieldX, ShieldQuestion, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { analyticsApi, type RiskSeverity } from '@/api/analytics';
import { cn } from '@/lib/utils';

const SEVERITY_STYLE: Record<RiskSeverity, { chip: string; Icon: typeof ShieldAlert }> = {
  critical: { chip: 'bg-rose-100 text-rose-800', Icon: ShieldX },
  high: { chip: 'bg-amber-100 text-amber-900', Icon: ShieldAlert },
  medium: { chip: 'bg-sky-100 text-sky-800', Icon: ShieldQuestion },
  low: { chip: 'bg-slate-100 text-slate-700', Icon: ShieldQuestion },
};

export function NotificationsBell() {
  const alerts = useQuery({
    queryKey: ['notifications', 'unread-probe'],
    queryFn: () => analyticsApi.riskAlerts(7),
    retry: false,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const items = alerts.data ?? [];
  const hasRecent = items.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          className="relative size-11 rounded-full"
        >
          <BellRing className="size-5" />
          {hasRecent && (
            <span
              className="absolute right-2.5 top-2.5 size-2 rounded-full bg-[#ff7a59] ring-2 ring-white"
              aria-label="Recent activity"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-baseline justify-between border-b border-[color:var(--kz-color-border-subtle)] px-3 py-2">
          <p className="text-sm font-semibold">Notifications</p>
          <span className="text-[10px] uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">last 7 days</span>
        </div>
        <div className="max-h-72 overflow-y-auto p-2">
          {alerts.isPending && (
            <p className="px-2 py-3 text-xs text-[color:var(--kz-color-ink-muted)]">Loading…</p>
          )}
          {!alerts.isPending && items.length === 0 && (
            <p className="px-2 py-3 text-xs text-[color:var(--kz-color-ink-muted)]">
              All quiet — no risk alerts in the last week.
            </p>
          )}
          {items.map((a) => {
            const style = SEVERITY_STYLE[a.severity];
            return (
              <div key={a.category} className="flex items-start gap-2 rounded-md px-2 py-2 hover:bg-[color:var(--kz-color-surface-sunken)]">
                <span className={cn('mt-0.5 grid size-6 shrink-0 place-items-center rounded-full', style.chip)}>
                  <style.Icon className="size-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium">{a.label}</p>
                    <span className={cn('shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium', style.chip)}>
                      {a.count}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-[color:var(--kz-color-ink-muted)]" title={a.advice}>
                    {a.advice}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-[color:var(--kz-color-border-subtle)] p-2">
          <Button asChild variant="ghost" size="sm" className="w-full justify-between">
            <Link to="/timeline">
              See full timeline
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
